import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { isV2SupportDeprecatedPath, V2_SUPPORT_DEPRECATED_FLAG } from '@/constants/featureFlags'
import { useAlertContext, useFeatureFlagsContext, useUserContext } from '@/context'
import { NotificationsQueries } from '@/graphql/queries/notifications.queries'
import { NotificationsSubscription } from '@/graphql/subscriptions/notifications.subscriptions'
import { useNotification } from '@/hooks'
import { useGraphql } from '@/hooks/useGraphql'
import { useSubscriptions } from '@/hooks/useSubscriptions'
import { APP_TYPE } from '@/types/User'
import {
  notificationLabel,
  notificationPath,
  onNotificationClick,
} from '@/views/NotificationAlerts/NotificationAlerts.data'

import { DesktopSidebarNavigation } from './DesktopSidebarNavigation'
import { MobileSidebarNavigation } from './MobileSidebarNavigation'

interface NavigationProps {
  activeApp: APP_TYPE
  minimize: boolean
  smallScreen: boolean
  toggleSideBar: () => void
}

export function Sidebar({ minimize = false, toggleSideBar, smallScreen, activeApp }: NavigationProps) {
  const navigate = useNavigate()
  const subscriptionRef = useRef<any>(null)
  const { user } = useUserContext()

  const { notify, close } = useNotification()
  const { setAlert } = useAlertContext()
  const { isEnabled } = useFeatureFlagsContext()
  const supportDeprecated = isEnabled(V2_SUPPORT_DEPRECATED_FLAG)

  // Refs so the subscription callback always reads current values without
  // needing to tear down and recreate the subscription when they change.
  const notifyRef = useRef(notify)
  const closeRef = useRef(close)
  const setAlertRef = useRef(setAlert)
  const supportDeprecatedRef = useRef(supportDeprecated)
  notifyRef.current = notify
  closeRef.current = close
  setAlertRef.current = setAlert
  supportDeprecatedRef.current = supportDeprecated

  const subscriptionObservable = useSubscriptions({
    query: NotificationsSubscription.subscribeToNotifications(),
  })

  const { data: notificationsData, refetch: refetchAlerts } = useGraphql<NotificationsQueries.GET.Response>(
    NotificationsQueries.QUERY_KEY,
    NotificationsQueries.getNotificationAlerts(),
    {
      userId: user?.id,
      viewed: false,
    },
    {
      enabled: !!user?.id,
      refetchOnWindowFocus: 'always',
    },
  )

  const refetchAlertsRef = useRef(refetchAlerts)
  refetchAlertsRef.current = refetchAlerts

  useEffect(() => {
    if (!user?.id) return

    subscriptionRef.current = subscriptionObservable.subscribe({
      next: (data: { data: NotificationsSubscription.Notifications.Response }) => {
        if (data?.data.notifications) {
          const { message } = data?.data.notifications
          const routePath = notificationPath(data?.data.notifications)
          const isDeprecatedPath = supportDeprecatedRef.current && isV2SupportDeprecatedPath(routePath)
          const label = notificationLabel(data?.data.notifications)
          const onClick = onNotificationClick(data?.data.notifications, isDeprecatedPath)

          if (message) {
            notifyRef.current(label, {
              body: message || '',
              onClick,
            })
            setAlertRef.current(message || label, 'success', routePath)
          }
        }
        refetchAlertsRef.current()
      },
    })

    return () => {
      subscriptionRef.current?.unsubscribe()
      subscriptionRef.current = null
      closeRef.current?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const onNavLinkPress = (path: string) => {
    if (smallScreen) {
      toggleSideBar()
    }
    navigate(path)
  }

  const numberOfUnreadNotifications = notificationsData?.notificationAlerts?.length || 0

  return (
    <>
      {smallScreen ? (
        <MobileSidebarNavigation
          activeApp={activeApp}
          isUnreadNotifications={!!numberOfUnreadNotifications}
          minimize={minimize}
          numberOfUnreadNotifications={numberOfUnreadNotifications}
          onNavLinkPress={onNavLinkPress}
        />
      ) : (
        <DesktopSidebarNavigation
          activeApp={activeApp}
          isUnreadNotifications={!!numberOfUnreadNotifications}
          minimize={minimize}
          numberOfUnreadNotifications={numberOfUnreadNotifications}
          onNavLinkPress={onNavLinkPress}
          toggleSideBar={toggleSideBar}
        />
      )}
    </>
  )
}
